import React, { useLayoutEffect } from 'react';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import { PickPoints, type AnalysisType } from 'config/analysisConfig';
import { colorandopacitycontrol2D } from 'utils/vtkUtilites';
import { registerRenderer, scheduleRender } from '../renderState';
import {
  labelorlineonoff,
  resetCPRView,
  updateRenderWindow,
} from '../Renderers/vtkManagers';
import type { ContextPool, CPRContext } from '../Renderers/vtkManagers';
import type { CaptureSetterType, CprPanelMode } from './cprPanelTypes';
import type { Label } from 'config/config';

export function useCprRendererRegistration({
  rendererId,
  context,
  analysisType,
  contextPool,
  mainIndex,
  mode,
  branchId,
  panelState,
  updateCPRState,
  refSetter,
  setGogo,
  setresetref,
  setlabelonoff,
  setExternalFocusIndicator,
  applyBoundaryEditActorVisibility,
  overlayLabel,
  forceLabelVisibilityOff = false,
  disabled = false,
}: {
  rendererId: string;
  context: React.MutableRefObject<CPRContext | null>;
  analysisType: AnalysisType;
  contextPool: ContextPool;
  mainIndex: number;
  mode?: CprPanelMode;
  branchId: number;
  panelState: any;
  updateCPRState: (params: any) => void;
  refSetter: React.MutableRefObject<CaptureSetterType | undefined>;
  setGogo: React.Dispatch<React.SetStateAction<'STOP' | 'GO' | 'GOGO'>>;
  setresetref: React.Dispatch<React.SetStateAction<boolean>>;
  setlabelonoff: React.Dispatch<React.SetStateAction<boolean>>;
  setExternalFocusIndicator: React.Dispatch<React.SetStateAction<{
    visible: boolean;
    branchId: number | null;
    index: number;
  }>>;
  applyBoundaryEditActorVisibility: () => void;
  overlayLabel?: Partial<Label>;
  forceLabelVisibilityOff?: boolean;
  disabled?: boolean;
}) {
  useLayoutEffect(() => {
    if (disabled) return;
    if (!rendererId) return;

    const rendererEntry = {
      render: async (args?: any) => {
        const { type, realIdx, init } = args ?? {};
        if (!context.current) return;
        const { actorIds } = context.current;

        switch (type) {
          case 'externalFocusIndicator': {
            const { branchId: indicatorBranchId, index } = args ?? {};
            if (!Number.isFinite(index)) break;

            setExternalFocusIndicator({
              visible: true,
              branchId: Number.isFinite(indicatorBranchId)
                ? Number(indicatorBranchId)
                : null,
              index: Math.round(Number(index)),
            });
            break;
          }
          case 'reset':
            resetCPRView(context);
            setresetref((prev) => !prev);
            break;
          case 'updateRenderWindow':
            if (!context.current) return;
            if (analysisType === 'aorticValveCalcium') {
              labelorlineonoff(
                analysisType,
                contextPool,
                'aorta',
                false,
                forceLabelVisibilityOff ? false : overlayLabel?.aorta,
                'cpr',
              );
            } else {
              const forcedLabelVisibility = forceLabelVisibilityOff
                ? false
                : undefined;
              labelorlineonoff(
                analysisType,
                contextPool,
                'lumen',
                false,
                forcedLabelVisibility ?? overlayLabel?.lumen,
                'cpr',
              );
              if (analysisType === 'plaque') {
                labelorlineonoff(
                  analysisType,
                  contextPool,
                  'plaque',
                  false,
                  forcedLabelVisibility ?? overlayLabel?.plaque,
                  'cpr',
                );
                labelorlineonoff(
                  analysisType,
                  contextPool,
                  'plaqueoutline',
                  false,
                  forcedLabelVisibility ?? overlayLabel?.plaqueoutline,
                  'cpr',
                );
              }
              if (analysisType === 'fai') {
                labelorlineonoff(
                  analysisType,
                  contextPool,
                  'fai',
                  false,
                  forcedLabelVisibility ?? overlayLabel?.fai,
                  'cpr',
                );
              }
            }
            updateRenderWindow(context);
            applyBoundaryEditActorVisibility();
            scheduleRender(`MarkerLine.${mainIndex}.${mode}`, {
              type: 'reproject',
            });
            if (init) {
              scheduleRender(`MarkerLine.${mainIndex}.${mode}`, {
                type: 'updatePath',
                newPath: context.current.path,
              });
              const { label } = args;
              if (label !== undefined) setlabelonoff((prev) => !prev);
            }
            break;
          case 'captureISR':
            resetCPRView(context);
            if (args?.key !== undefined) {
              updateCPRState({
                mainIndex: { index: 0, branchId: args.key },
              });
              refSetter.current = args;
              setGogo('GO');
            }
            break;
          case 'polygonMove':
          case 'markerMove': {
            const { which, newPickedPoints } = args;
            if (!Array.isArray(newPickedPoints)) return;

            if (newPickedPoints.length !== 1) {
              const states = panelState.state[branchId];
              const baseIdx = Number.isFinite(realIdx) ? realIdx : 0;
              const newStates = [
                ...states.slice(0, baseIdx * 3),
                ...(newPickedPoints as PickPoints[]),
                ...states.slice((baseIdx + 1) * 3),
              ];
              updateCPRState({ indexedStates: { [branchId]: newStates } });
            } else {
              updateCPRState({
                indexedState: {
                  mainIndex: {
                    branchId,
                    index: which,
                  },
                  pickPoint: newPickedPoints[0],
                },
              });
            }
            break;
          }
          case 'colorchange': {
            const { target, newSegmentation } = args;
            if (!actorIds) return;
            const actor = actorIds['CPRPaintActor'] as vtkImageSlice;
            if (
              colorandopacitycontrol2D(
                analysisType,
                target,
                newSegmentation,
                actor,
              )
            ) {
              updateRenderWindow(context);
            }
            break;
          }
        }
      },
    };

    return registerRenderer(rendererId, rendererEntry);
  });
}
